import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ShoppingCart,
  X,
  Trash2,
  CreditCard,
  Tag,
  ArrowRight,
  Lock,
  Ticket,
  QrCode,
  Sparkles,
  Zap
} from 'lucide-react'

interface CartItem {
  id: string
  title: string
  instructor?: string
  thumbnail?: string
  price: number
  originalPrice?: number
  quantity?: number
}

export default function UserCart() {
  const navigate = useNavigate()
  const [cart, setCart] = useState<CartItem[]>([])
  const [loading, setLoading] = useState(true)
  const [couponCode, setCouponCode] = useState('')
  const [discount, setDiscount] = useState(0)

  useEffect(() => {
    loadCart()
  }, [])

  const loadCart = () => {
    try {
      const savedCart = JSON.parse(localStorage.getItem('userCart') || '[]')
      setCart(savedCart.map((item: any) => ({ ...item, quantity: item.quantity || 1 })))
    } catch (error) {
      console.error('Error loading cart:', error)
      setCart([])
    } finally {
      setLoading(false)
    }
  }

  const removeFromCart = (courseId: string) => {
    const updatedCart = cart.filter(item => item.id !== courseId)
    setCart(updatedCart)
    localStorage.setItem('userCart', JSON.stringify(updatedCart))
    // Update header badge
    window.dispatchEvent(new CustomEvent('cart-updated', { detail: { count: updatedCart.length } }))
  }

  const clearCart = () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      setCart([])
      localStorage.setItem('userCart', JSON.stringify([]))
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: { count: 0 } }))
    }
  }

  const applyCoupon = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        alert('Please login to apply coupon')
        return
      }

      const response = await fetch('/api/coupons/validate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: couponCode })
      })

      if (response.ok) {
        const data = await response.json()
        setDiscount(data.discount || 0)
        alert(`Coupon applied! ${data.discount}% discount`)
      } else {
        alert('Invalid coupon code')
        setDiscount(0)
      }
    } catch (error) {
      console.error('Error applying coupon:', error)
      alert('Failed to apply coupon')
      setDiscount(0)
    }
  }

  const calculateTotal = () => {
    const subtotal = cart.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0)
    const discountAmount = (subtotal * discount) / 100
    const total = subtotal - discountAmount
    return { subtotal, discountAmount, total }
  }

  const handleCheckout = () => {
    if (cart.length === 0) {
      alert('Your cart is empty!')
      return
    }

    const token = localStorage.getItem('token')
    if (!token) {
      localStorage.setItem('redirectAfterLogin', '/user/cart')
      navigate('/user/login', { state: { from: '/user/cart' } })
      return
    }

    navigate('/user/checkout')
  }

  const { subtotal, discountAmount, total } = calculateTotal()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-2 bg-violet-600 rounded-full"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">
            Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-fuchsia-600">Learning Pass</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-lg">
            {cart.length} {cart.length === 1 ? 'ticket' : 'tickets'} to your future success.
          </p>
        </div>
        {cart.length > 0 && (
          <button
            onClick={clearCart}
            className="text-slate-400 hover:text-red-500 font-medium flex items-center gap-2 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Clear All
          </button>
        )}
      </div>

      {cart.length > 0 ? (
        <div className="flex flex-col lg:flex-row gap-8 items-start">

          {/* Left Side: The Tickets */}
          <div className="flex-1 w-full space-y-6">
            {cart.map((item, index) => (
              <div
                key={item.id}
                className="group relative bg-white dark:bg-slate-800 rounded-3xl overflow-hidden shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-700 transition-all hover:-translate-y-1 duration-300"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Ticket Layout */}
                <div className="flex flex-col sm:flex-row h-full">

                  {/* Left Stub (Image) */}
                  <div className="sm:w-48 relative h-48 sm:h-auto bg-slate-900">
                    <img
                      src={item.thumbnail || ''}
                      alt={item.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent sm:bg-gradient-to-r" />

                    {/* Perforation Line (Mobile: Bottom, Desktop: Right) */}
                    <div className="absolute bottom-0 sm:bottom-auto sm:right-0 w-full sm:w-px h-px sm:h-full bg-transparent border-t-2 sm:border-t-0 sm:border-r-2 border-dashed border-white/30 z-10 flex items-center justify-center">
                      <div className="absolute -left-3 sm:-top-3 w-6 h-6 bg-slate-50 dark:bg-slate-900 rounded-full"></div>
                      <div className="absolute -right-3 sm:-bottom-3 w-6 h-6 bg-slate-50 dark:bg-slate-900 rounded-full"></div>
                    </div>
                  </div>

                  {/* Right Main Content */}
                  <div className="flex-1 p-6 sm:p-8 flex flex-col justify-between relative bg-white dark:bg-slate-800">
                    {/* Decorative Barcode */}
                    <div className="absolute top-6 right-6 opacity-10 dark:opacity-20 pointer-events-none">
                      <QrCode className="w-16 h-16" />
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400">
                          ADMIT ONE
                        </span>
                        {item.instructor && (
                          <span className="text-xs font-mono text-slate-400">
                            INSTR: {item.instructor.toUpperCase().slice(0, 10)}
                          </span>
                        )}
                      </div>

                      <h3
                        className="text-xl font-bold text-slate-900 dark:text-white mb-2 pr-12 cursor-pointer hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                        onClick={() => navigate(`/user/courses/${item.id}`)}
                      >
                        {item.title}
                      </h3>
                    </div>

                    <div className="flex items-end justify-between mt-4 pt-4 border-t border-dashed border-slate-200 dark:border-slate-700">
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Price</span>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-mono font-bold text-slate-900 dark:text-white">
                            ₹{item.price.toLocaleString()}
                          </span>
                          {item.originalPrice && (
                            <span className="text-sm font-mono text-slate-400 line-through decoration-red-500/50">
                              ₹{item.originalPrice.toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-xs font-bold text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-2 rounded-lg transition-colors flex items-center gap-1"
                      >
                        <X className="w-3 h-3" /> REMOVE
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right Side: The Manifest (Summary) */}
          <div className="w-full lg:w-96 flex-shrink-0">
            <div className="sticky top-6 bg-slate-900 text-white rounded-3xl p-6 shadow-2xl shadow-slate-900/20 overflow-hidden relative">
              {/* Background Glow */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-violet-600/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

              <div className="relative z-10">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-violet-400" />
                  Order Manifest
                </h2>

                <div className="space-y-4 mb-8 font-mono text-sm">
                  <div className="flex justify-between text-slate-400">
                    <span>SUBTOTAL</span>
                    <span>₹{subtotal.toLocaleString()}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-green-400">
                      <span>DISCOUNT ({discount}%)</span>
                      <span>-₹{discountAmount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="h-px bg-slate-800 my-2"></div>
                  <div className="flex justify-between text-lg font-bold text-white">
                    <span>TOTAL</span>
                    <span>₹{total.toLocaleString()}</span>
                  </div>
                </div>

                {/* Coupon Input */}
                <div className="mb-8">
                  <div className="relative">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="PROMO CODE"
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm font-mono text-white placeholder-slate-500 focus:outline-none focus:border-violet-500 transition-colors uppercase"
                    />
                    <button
                      onClick={applyCoupon}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-slate-700 hover:bg-violet-600 rounded-lg text-xs font-bold transition-colors"
                    >
                      APPLY
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  className="w-full bg-white text-slate-900 hover:bg-violet-50 font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 group"
                >
                  <span>Proceed to Checkout</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>

                <div className="mt-6 flex items-center justify-center gap-2 text-[10px] text-slate-500 font-mono uppercase tracking-wider">
                  <Lock className="w-3 h-3" />
                  Encrypted & Secure
                </div>
              </div>
            </div>
          </div>

        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-24 px-4 bg-white dark:bg-slate-800 rounded-3xl border border-dashed border-slate-200 dark:border-slate-700">
          <div className="w-24 h-24 bg-slate-50 dark:bg-slate-900 rounded-full flex items-center justify-center mb-6 relative group">
            <div className="absolute inset-0 bg-violet-500/20 rounded-full blur-xl group-hover:blur-2xl transition-all"></div>
            <ShoppingCart className="w-10 h-10 text-slate-300 dark:text-slate-600" />
          </div>

          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Your pass is blank
          </h3>
          <p className="text-slate-500 dark:text-slate-400 text-center max-w-md mb-8">
            You haven't added any courses to your learning pass yet. Explore our catalog to find your next skill.
          </p>

          <button
            onClick={() => navigate('/user/courses')}
            className="px-8 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold hover:scale-105 transition-transform flex items-center gap-2"
          >
            <Zap className="w-4 h-4 fill-current" />
            Start Exploring
          </button>
        </div>
      )}
    </div>
  )
}